<template>
  <!-- Main Content -->
  <div class="flex-1 p-4 lg:p-8 bg-[#1F1F1F]">
    <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 lg:mb-8 gap-4">
      <h1 class="text-2xl lg:text-3xl font-bold text-[#F5BE32]">{{ $t('admin.orders.title') }}</h1>
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:gap-4">
        <!-- Search Bar -->
        <div class="relative">
          <input 
            v-model="searchQuery" 
            @input="handleSearch"
            type="text" 
            :placeholder="$t('admin.orders.searchPlaceholder')" 
            class="px-4 py-2 bg-[#4F4F4F] border border-[#F5BE32] text-white rounded-lg focus:outline-none focus:border-[#E5AE22] w-full sm:w-64"
          />
          <svg class="absolute right-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
          </svg>
        </div>
        
        <!-- Filter Dropdown -->
        <div class="relative">
          <button 
            @click="showFilterDropdown = !showFilterDropdown"
            class="flex items-center justify-center gap-2 px-4 py-2 bg-[#4F4F4F] border border-[#F5BE32] text-[#F5BE32] rounded-lg hover:bg-[#1F1F1F] transition-colors w-full sm:w-auto"
          >
            <span>{{ getFilterLabel() }}</span>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          
          <!-- Filter Dropdown Menu -->
          <div v-if="showFilterDropdown" class="absolute right-0 mt-2 w-48 bg-[#4F4F4F] border border-[#F5BE32] rounded-lg shadow-lg z-10">
            <div class="py-2">
              <button 
                @click="setFilter('all', 'all')"
                class="block w-full text-left px-4 py-2 text-white hover:bg-[#1F1F1F]"
              >
                {{ $t('admin.orders.allOrders') }}
              </button>
              <div class="border-t border-[#1F1F1F] my-1"></div>
              <div class="px-4 py-1 text-xs text-gray-400">{{ $t('admin.orders.status') }}</div>
              <button 
                v-for="status in statusFilterOptions" 
                :key="status.value"
                @click="setFilter('status', status.value)"
                class="block w-full text-left px-4 py-2 text-white hover:bg-[#1F1F1F]"
              >
                {{ $t(status.label) }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5BE32]"></div>
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="bg-red-500 text-white p-4 rounded-lg mb-6">
      {{ error }}
    </div>
    
    <!-- Orders Table -->
    <div v-else class="bg-[#4F4F4F] rounded-xl p-4 lg:p-5 border border-[#1F1F1F]">
      <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 lg:mb-5 gap-3">
        <h2 class="text-lg lg:text-xl font-semibold text-white">{{ $t('admin.overview.latestOrders') }}</h2>
        <div class="text-sm text-gray-300">
          {{ orders.length }} {{ $t('admin.orders.ordersFound') }}
        </div>
      </div>
      
      <div v-if="orders.length === 0" class="text-center py-12 text-gray-400">
        {{ $t('admin.orders.noOrdersFound') }}
      </div>
      
      <div v-else>
                 <table class="w-full table-fixed">
                     <thead>
             <tr class="border-b border-[#1F1F1F]">
               <th class="text-left p-2 lg:p-3 text-gray-300 font-semibold text-xs lg:text-sm w-[15%]">{{ $t('admin.orders.tableHeaders.orderNumber') }}</th>
               <th class="text-left p-2 lg:p-3 text-gray-300 font-semibold text-xs lg:text-sm w-[12%]">{{ $t('admin.orders.tableHeaders.time') }}</th>
               <th class="text-left p-2 lg:p-3 text-gray-300 font-semibold text-xs lg:text-sm w-[25%]">{{ $t('admin.orders.tableHeaders.customer') }}</th>
               <th class="text-left p-2 lg:p-3 text-gray-300 font-semibold text-xs lg:text-sm w-[12%]">{{ $t('admin.orders.tableHeaders.amount') }}</th>
               <th class="text-left p-2 lg:p-3 text-gray-300 font-semibold text-xs lg:text-sm w-[12%]">{{ $t('admin.orders.tableHeaders.status') }}</th>
               <th class="text-left p-2 lg:p-3 text-gray-300 font-semibold text-xs lg:text-sm w-[12%]">{{ $t('admin.orders.tableHeaders.actions') }}</th>
             </tr>
           </thead>
          <tbody>
                         <tr 
               v-for="order in orders" 
               :key="order.id"
               class="border-b border-[#1F1F1F] cursor-pointer hover:bg-[#1F1F1F]"
               @click="showOrderDetails(order)"
             >
               <td class="p-2 lg:p-3 text-white text-xs lg:text-sm truncate" :title="order.orderId || order.id">{{ order.orderId || order.id }}</td>
               <td class="p-2 lg:p-3 text-white text-xs lg:text-sm">{{ order.formattedTime }}</td>
               <td class="p-2 lg:p-3 text-white text-xs lg:text-sm truncate" :title="order.customerName || 'N/A'">{{ order.customerName || 'N/A' }}</td>
               <td class="p-2 lg:p-3 text-white text-xs lg:text-sm">{{ order.formattedAmount }}</td>
               <td class="p-2 lg:p-3">
                 <span 
                   :class="`inline-flex items-center gap-1 px-2 lg:px-3 py-1 ${order.displayStatus.bgColor} ${order.displayStatus.textColor} text-xs font-medium rounded-full whitespace-nowrap`"
                 >
                   <div :class="`w-2 h-2 ${order.displayStatus.dotColor} rounded-full`"></div>
                   {{ $t('admin.orders.statuses.' + order.status) }}
                 </span>
               </td>
               <td class="p-2 lg:p-3">
                 <div class="flex flex-col gap-1">
                   <button 
                     @click.stop="openEditModal(order)"
                     class="px-2 py-1 bg-[#F5BE32] text-[#1F1F1F] text-xs rounded hover:bg-[#E5AE22] whitespace-nowrap"
                   >
                     {{ $t('admin.orders.actions.update') }}
                   </button>
                   <button 
                     @click.stop="deleteOrderItem(order.id)"
                     class="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 whitespace-nowrap"
                   >
                     {{ $t('admin.orders.actions.delete') }}
                   </button>
                 </div>
               </td>
             </tr>
          </tbody>
        </table>
      </div>


    </div>
  </div>

  <!-- Order Details Modal -->
  <div v-if="selectedOrder" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-[#4F4F4F] rounded-xl p-6 w-96 max-h-[80vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-white">{{ $t('admin.orders.orderDetails.title') }}</h2>
        <button @click="selectedOrder = null" class="text-white hover:text-gray-300">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Order Details -->
      <div class="space-y-3 mb-6">
        <div class="flex justify-between">
          <span class="text-white">{{ $t('admin.orders.orderDetails.orderNumber') }}</span>
          <span class="text-white font-medium">{{ selectedOrder.orderId || selectedOrder.id }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-white">{{ $t('admin.orders.orderDetails.date') }}</span>
          <span class="text-white font-medium">{{ selectedOrder.formattedDate }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-white">{{ $t('admin.orders.orderDetails.time') }}</span>
          <span class="text-white font-medium">{{ selectedOrder.formattedTime }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-white">{{ $t('admin.orders.orderDetails.customer') }}</span>
          <span class="text-white font-medium">{{ selectedOrder.customerName || 'N/A' }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-white">{{ $t('admin.orders.orderDetails.phone') }}</span>
          <span class="text-white font-medium">{{ selectedOrder.customerPhone || 'N/A' }}</span>
        </div>
      </div>

      <!-- Divider -->
      <div class="border-b border-[#1F1F1F] mb-6"></div>

      <!-- Ordered Items -->
      <div class="space-y-3 mb-6">
        <h3 class="text-white font-semibold mb-3">{{ $t('admin.orders.orderDetails.orderedItems') }}</h3>
        <div v-if="selectedOrder.dishes && selectedOrder.dishes.length > 0">
          <div 
            v-for="item in selectedOrder.dishes" 
            :key="item.id"
            class="flex justify-between"
          >
          <div class="flex flex-col">  
            <span class="text-white">
              {{ getDishDisplayName(item) }} ({{ item.count }}x)
            </span>
             <span class="text-white">{{ item.specialRequest }}</span></div>
            
            <span class="text-white"> {{ (item.price * item.count).toFixed(2) }}</span>

          </div>
        </div>
        <div v-else class="text-gray-400 text-sm">{{ $t('admin.orders.orderDetails.noItemsFound') }}</div>
      </div>

      <!-- Divider -->
      <div class="border-b border-[#1F1F1F] mb-6"></div>

      <!-- Financial Summary -->
      <div class="space-y-3 mb-8">
        <div class="flex justify-between">
          <span class="text-white">{{ $t('admin.orders.orderDetails.subtotal') }}</span>
          <span class="text-white">{{ (selectedOrder.totalAmount - (selectedOrder.tax || 0)).toFixed(2) }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-white">{{ $t('admin.orders.orderDetails.tax') }}</span>
          <span class="text-white">{{ (selectedOrder.tax || 0).toFixed(2) }}</span>
        </div>
        <div class="flex justify-between text-lg font-semibold">
          <span class="text-white">{{ $t('admin.orders.orderDetails.total') }}</span>
          <span class="text-white">{{ selectedOrder.formattedAmount }}</span>
        </div>
      </div>

      <!-- Status Update -->
      <div class="mb-6">
        <label class="block text-white text-sm font-medium mb-2">{{ $t('admin.orders.orderDetails.updateStatus') }}</label>
        <select 
          v-model="selectedOrder.status"
          @change="updateStatus(selectedOrder.id, selectedOrder.status)"
          class="w-full px-3 py-2 bg-[#1F1F1F] border border-[#F5BE32] text-white rounded-lg focus:outline-none focus:border-[#E5AE22]"
        >
          <option
            v-for="status in getStatusOptions(selectedOrder.orderType)"
            :key="status.value"
            :value="status.value"
          >
            {{ $t(status.label) }}
          </option>
        </select>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-3">
        <button 
          @click="selectedOrder = null"
          class="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-600 transition-colors"
        >
          {{ $t('admin.orders.orderDetails.close') }}
        </button>
        <!-- <button 
          @click="exportOrderData(selectedOrder.id)"
          class="flex-1 bg-[#F5BE32] text-[#1F1F1F] py-3 px-4 rounded-lg font-medium hover:bg-[#E5AE22] transition-colors"
        >
          {{ $t('admin.orders.orderDetails.export') }}
        </button> -->
      </div>
    </div>
  </div>

  <!-- Order Edit Modal -->
  <div v-if="editingOrder" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-[#4F4F4F] rounded-xl p-6 w-[32rem] max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-white">{{ $t('admin.orders.editOrder.title') }}</h2>
        <button @click="closeEditModal" class="text-white hover:text-gray-300">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Edit Form -->
      <form @submit.prevent="saveOrderChanges" class="space-y-4">
        <!-- Customer Information -->
        <div class="space-y-3">
          <h3 class="text-white font-medium text-sm">{{ $t('admin.orders.editOrder.customerInfo') }}</h3>
          
          <div>
            <label class="block text-white text-xs mb-1">{{ $t('admin.orders.editOrder.customerName') }}</label>
            <input 
              v-model="editForm.customerName"
              type="text"
              class="w-full px-3 py-2 bg-[#1F1F1F] border border-[#666] text-white rounded-lg focus:outline-none focus:border-[#F5BE32] text-sm"
            />
          </div>
          
          <div>
            <label class="block text-white text-xs mb-1">{{ $t('admin.orders.editOrder.customerPhone') }}</label>
            <input 
              v-model="editForm.customerPhone"
              type="tel"
              class="w-full px-3 py-2 bg-[#1F1F1F] border border-[#666] text-white rounded-lg focus:outline-none focus:border-[#F5BE32] text-sm"
            />
          </div>
          
          <div>
            <label class="block text-white text-xs mb-1">{{ $t('admin.orders.editOrder.customerEmail') }}</label>
            <input 
              v-model="editForm.customerEmail"
              type="email"
              class="w-full px-3 py-2 bg-[#1F1F1F] border border-[#666] text-white rounded-lg focus:outline-none focus:border-[#F5BE32] text-sm"
            />
          </div>
        </div>

        <!-- Order Details -->
        <div class="space-y-3">
          <h3 class="text-white font-medium text-sm">{{ $t('admin.orders.editOrder.orderDetails') }}</h3>
          
          <div>
            <label class="block text-white text-xs mb-1">{{ $t('admin.orders.editOrder.status') }}</label>
            <select 
              v-model="editForm.status"
              class="w-full px-3 py-2 bg-[#1F1F1F] border border-[#666] text-white rounded-lg focus:outline-none focus:border-[#F5BE32] text-sm"
            >
              <option value="pending">{{ $t('admin.orders.statuses.pending') }}</option>
              <option value="confirmed">{{ $t('admin.orders.statuses.confirmed') }}</option>
              <option value="preparing">{{ $t('admin.orders.statuses.preparing') }}</option>
              <option value="ready">{{ $t('admin.orders.statuses.ready') }}</option>
              <option value="served">{{ $t('admin.orders.statuses.served') }}</option>
              <option value="cancelled">{{ $t('admin.orders.statuses.cancelled') }}</option>
              <option value="delivered">{{ $t('admin.orders.statuses.delivered') }}</option>
            </select>
          </div>

          <div v-if="true">
            <label class="block text-white text-xs mb-1">{{ $t('admin.orders.editOrder.deliveryAddress') }}</label>
            <textarea 
              v-model="editForm.deliveryAddress"
              rows="2"
              class="w-full px-3 py-2 bg-[#1F1F1F] border border-[#666] text-white rounded-lg focus:outline-none focus:border-[#F5BE32] text-sm resize-none"
            ></textarea>
          </div>

          <div>
            <label class="block text-white text-xs mb-1">{{ $t('admin.orders.editOrder.specialRequest') }}</label>
            <textarea 
              v-model="editForm.specialRequest"
              rows="2"
              class="w-full px-3 py-2 bg-[#1F1F1F] border border-[#666] text-white rounded-lg focus:outline-none focus:border-[#F5BE32] text-sm resize-none"
            ></textarea>
          </div>
        </div>

        <!-- Ordered Items Section -->
        <div class="space-y-3">
                     <div class="flex justify-between items-center">
             <h3 class="text-white font-medium text-sm">{{ $t('admin.orders.editOrder.orderedItems') }}</h3>
             <button 
               type="button"
               @click="addNewDish"
               class="text-xs bg-[#F5BE32] text-[#1F1F1F] px-2 py-1 rounded hover:bg-[#E5AE22] transition-colors"
             >
               {{ $t('admin.orders.editOrder.addItem') }}
             </button>
           </div>
           <div class="text-xs text-gray-400 mb-2">
             {{ $t('admin.orders.editOrder.selectFromMenu') || 'Select dishes from the menu below, or leave empty for custom items' }}
           </div>
          
          <div v-if="editForm.dishes && editForm.dishes.length > 0" class="space-y-2">
            <div 
              v-for="(dish, index) in editForm.dishes" 
              :key="`dish-${index}`"
              class="bg-[#1F1F1F] p-3 rounded-lg border border-[#666]"
            >
                             <div class="grid grid-cols-12 gap-2 items-center">
                 <!-- Dish Name -->
                 <div class="col-span-5">
                                       <select 
                      v-model="dish.menuItemId"
                      @change="onMenuItemSelect(dish, dish.menuItemId)"
                      class="w-full px-2 py-1 bg-[#2A2A2A] border border-[#555] text-white rounded text-xs focus:outline-none focus:border-[#F5BE32]"
                    >
                      <option value="">{{ dish.dishName || ($t('admin.orders.editOrder.selectDish') || 'Select Dish') }}</option>
                      <optgroup 
                        v-for="(items, category) in menuItemsByCategory" 
                        :key="category" 
                        :label="category.charAt(0).toUpperCase() + category.slice(1)"
                      >
                        <option 
                          v-for="item in items" 
                          :key="item.id" 
                          :value="item.id"
                        >
                          {{ item.name }} - LE {{ item.price.toFixed(2) }}
                        </option>
                      </optgroup>
                                        </select>
                  </div>
                 <!-- Quantity -->
                <div class="col-span-2">
                  <input 
                    v-model.number="dish.count"
                    type="number"
                    min="1"
                    placeholder="Qty"
                    class="w-full px-2 py-1 bg-[#2A2A2A] border border-[#555] text-white rounded text-xs focus:outline-none focus:border-[#F5BE32]"
                    @input="calculateTotals"
                  />
                </div>
                
                                 <!-- Price -->
                 <div class="col-span-2">
                   <input 
                     v-model.number="dish.price"
                     type="number"
                     step="0.01"
                     placeholder="Price"
                     :readonly="!!dish.menuItemId"
                     :class="`w-full px-2 py-1 bg-[#2A2A2A] border border-[#555] text-white rounded text-xs focus:outline-none focus:border-[#F5BE32] ${dish.menuItemId ? 'opacity-75 cursor-not-allowed' : ''}`"
                     @input="calculateTotals"
                   />
                 </div>
                
                <!-- Total for this item -->
                <div class="col-span-2">
                  <span class="text-white text-xs">{{ ((dish.price || 0) * (dish.count || 0)).toFixed(2) }}</span>
                </div>
                
                <!-- Remove button -->
                <div class="col-span-1">
                  <button 
                    type="button"
                    @click="removeDish(index)"
                    class="text-red-400 hover:text-red-300 text-xs"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <!-- Special Request for this dish -->
              <div class="mt-2">
                <input 
                  v-model="dish.specialRequest"
                  type="text"
                  :placeholder="$t('admin.orders.editOrder.dishSpecialRequest')"
                  class="w-full px-2 py-1 bg-[#2A2A2A] border border-[#555] text-white rounded text-xs focus:outline-none focus:border-[#F5BE32]"
                />
              </div>
            </div>
          </div>
          
          <div v-else class="text-gray-400 text-xs text-center py-4">
            {{ $t('admin.orders.editOrder.noItems') }}
          </div>
        </div>

        <!-- Payment Information -->
        <div class="space-y-3">
          <h3 class="text-white font-medium text-sm">{{ $t('admin.orders.editOrder.paymentInfo') }}</h3>
          
          <div>
            <label class="block text-white text-xs mb-1">{{ $t('admin.orders.editOrder.paymentMethod') }}</label>
            <select 
              v-model="editForm.paymentMethod"
              class="w-full px-3 py-2 bg-[#1F1F1F] border border-[#666] text-white rounded-lg focus:outline-none focus:border-[#F5BE32] text-sm"
            >
              <option value="cash">{{ $t('admin.orders.paymentMethods.cash') }}</option>
              <option value="card">{{ $t('admin.orders.paymentMethods.card') }}</option>
              <option value="online">{{ $t('admin.orders.paymentMethods.online') }}</option>
            </select>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-white text-xs mb-1">{{ $t('admin.orders.editOrder.subtotal') }}</label>
              <input 
                v-model.number="editForm.subtotal"
                type="number"
                step="0.01"
                class="w-full px-3 py-2 bg-[#1F1F1F] border border-[#666] text-white rounded-lg focus:outline-none focus:border-[#F5BE32] text-sm"
              />
            </div>
            
                         <div>
               <label class="block text-white text-xs mb-1">{{ $t('admin.orders.editOrder.serviceFee') }}</label>
               <input 
                 v-model.number="editForm.serviceFee"
                 type="number"
                 step="0.01"
                 class="w-full px-3 py-2 bg-[#1F1F1F] border border-[#666] text-white rounded-lg focus:outline-none focus:border-[#F5BE32] text-sm"
                 @input="calculateTotals"
               />
             </div>
          </div>
          
          <div>
            <label class="block text-white text-xs mb-1">{{ $t('admin.orders.editOrder.totalAmount') }}</label>
            <input 
              v-model.number="editForm.totalAmount"
              type="number"
              step="0.01"
              class="w-full px-3 py-2 bg-[#1F1F1F] border border-[#666] text-white rounded-lg focus:outline-none focus:border-[#F5BE32] text-sm"
            />
          </div>
        </div>

        <!-- Admin Notes -->
        <div>
          <label class="block text-white text-xs mb-1">{{ $t('admin.orders.editOrder.adminNotes') }}</label>
                     <textarea 
             v-model="editForm.adminNotes"
             rows="2"
             class="w-full px-3 py-2 bg-[#1F1F1F] border border-[#666] text-white rounded-lg focus:outline-none focus:border-[#F5BE32] text-sm resize-none"
           ></textarea>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 pt-4">
          <button 
            type="button"
            @click="closeEditModal"
            class="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-600 transition-colors text-sm"
          >
            {{ $t('admin.orders.editOrder.cancel') }}
          </button>
          <button 
            type="submit"
            :disabled="isUpdating"
            class="flex-1 bg-[#F5BE32] text-[#1F1F1F] py-3 px-4 rounded-lg font-medium hover:bg-[#E5AE22] transition-colors disabled:opacity-50 text-sm"
          >
            {{ isUpdating ? $t('admin.orders.editOrder.saving') : $t('admin.orders.editOrder.saveChanges') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import adminOrderService from '../services/adminOrderService';
import menuService from '../services/menuService';
import { getTranslatedDishName } from "@/utils/dishTranslation"; // <-- Import

export default {
name: 'AdminOrders',
data() {
  return {
    orders: [],
    selectedOrder: null,
    editingOrder: null,
    editForm: {
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      status: 'pending',
      deliveryAddress: '',
      specialRequest: '',
      paymentMethod: 'cash',
      subtotal: 0,
      serviceFee: 0,
      totalAmount: 0,
      adminNotes: '',
      dishes: []
    },
    isUpdating: false,
    loading: false,
    loadingMore: false,
    error: null,
    searchQuery: '',
    searchTimeout: null,
    showFilterDropdown: false,
    currentFilter: { type: 'all', value: 'all' },
    hasMore: false,
    lastDoc: null,
         statusConfig: adminOrderService.getStatusBadgeConfig(),
     menuItems: [],
     menuItemsByCategory: {},
     statusFilterOptions: [
        { value: 'pending', label: 'admin.orders.statuses.pending' },
        { value: 'preparing', label: 'admin.orders.statuses.preparing' },
        { value: 'confirmed', label: 'admin.orders.statuses.confirmed' },
        { value: 'cancelled', label: 'admin.orders.statuses.cancelled' },
        { value: 'delivered', label: 'admin.orders.statuses.delivered' },
        { value: 'notStarted', label: 'admin.orders.statuses.notStarted' },
        { value: 'inProgress', label: 'admin.orders.statuses.inProgress' },
        { value: 'served', label: 'admin.orders.statuses.served' }
      ]
  }
},
 async mounted() {
   await this.loadOrders();
   await this.loadMenuItems();
 },
methods: {
     async loadMenuItems() {
     try {
       this.menuItems = await menuService.getMenuItems();
       this.menuItemsByCategory = await menuService.getMenuItemsByCategory();
     } catch (error) {
       console.error('Error loading menu items:', error);
     }
   },

   async loadOrders() {
    this.loading = true;
    this.error = null;

    try {
      const params = {
        limit: 20,
        ...this.getFilterParams()
      };

      const result = await adminOrderService.fetchOrders(params);
      this.orders = result.orders.map(order => {
        // Always set orderType to 'delivery' for deliveryOrders collection
        return {
          ...adminOrderService.formatOrderForDisplay(order),
          orderType: 'delivery'
        };
      });
      this.hasMore = result.hasMore;
      this.lastDoc = result.lastDoc;
    } catch (error) {
      this.error = error.message;
      console.error('Error loading orders:', error);
    } finally {
      this.loading = false;
    }
  },

  async loadMore() {
    if (!this.hasMore || this.loadingMore) return;
    
    this.loadingMore = true;
    
    try {
      const params = {
        limit: 20,
        startAfter: this.lastDoc,
        ...this.getFilterParams()
      };
      
      const result = await adminOrderService.fetchOrders(params);
      const newOrders = result.orders.map(order => adminOrderService.formatOrderForDisplay(order));
      this.orders.push(...newOrders);
      this.hasMore = result.hasMore;
      this.lastDoc = result.lastDoc;
    } catch (error) {
      this.error = error.message;
      console.error('Error loading more orders:', error);
    } finally {
      this.loadingMore = false;
    }
  },

  async handleSearch() {
    clearTimeout(this.searchTimeout);
    
    this.searchTimeout = setTimeout(async () => {
      if (this.searchQuery.trim().length < 2) {
        await this.loadOrders();
        return;
      }
      
      try {
        const result = await adminOrderService.searchOrders(this.searchQuery);
        this.orders = result.orders.map(order => adminOrderService.formatOrderForDisplay(order));
        this.hasMore = false;
      } catch (error) {
        this.error = error.message;
        console.error('Error searching orders:', error);
      }
    }, 500);
  },

  setFilter(type, value) {
    this.currentFilter = { type, value };
    this.showFilterDropdown = false;
    this.loadOrders();
  },

  getFilterLabel() {
    if (this.currentFilter.type === 'all') return this.$t('admin.orders.filter');
    if (this.currentFilter.type === 'status') {
      return this.statusConfig[this.currentFilter.value]?.label || this.$t('admin.orders.filter');
    }
    return this.$t('admin.orders.filter');
  },

  getFilterParams() {
    const params = {};
    if (this.currentFilter.type === 'status' && this.currentFilter.value !== 'all') {
      params.status = this.currentFilter.value;
    }
    return params;
  },



  getNextStatus(currentStatus) {
    const statusFlow = {
      pending: 'confirmed',
      confirmed: 'preparing',
      preparing: 'ready',
      ready: 'served',
      served: 'served'
    };
    return statusFlow[currentStatus] || currentStatus;
  },

  async updateStatus(orderId, newStatus) {
    try {
      await adminOrderService.updateOrderStatus(orderId, newStatus);
      
      // Update the order in the local array
      const orderIndex = this.orders.findIndex(order => order.id === orderId);
      if (orderIndex !== -1) {
        this.orders[orderIndex].status = newStatus;
        this.orders[orderIndex] = adminOrderService.formatOrderForDisplay(this.orders[orderIndex]);
      }
      
      // Update selected order if it's the same
      if (this.selectedOrder && this.selectedOrder.id === orderId) {
        this.selectedOrder.status = newStatus;
        this.selectedOrder = adminOrderService.formatOrderForDisplay(this.selectedOrder);
      }
    } catch (error) {
      this.error = error.message;
      console.error('Error updating order status:', error);
    }
  },

  async deleteOrderItem(orderId) {
    if (!confirm(this.$t('admin.orders.confirmDelete'))) return;
    
    try {
      await adminOrderService.deleteOrder(orderId, 'Deleted by admin');
      
      // Remove from local array
      this.orders = this.orders.filter(order => order.id !== orderId);
      
      // Close modal if it's the selected order
      if (this.selectedOrder && this.selectedOrder.id === orderId) {
        this.selectedOrder = null;
      }
    } catch (error) {
      this.error = error.message;
      console.error('Error deleting order:', error);
    }
  },

  showOrderDetails(order) {
    this.selectedOrder = { ...order };
  },

  async exportOrderData(orderId) {
    try {
      const result = await adminOrderService.exportOrders(null, null, 'json');
      const orderData = result.data.find(order => order.id === orderId);
      
      if (orderData) {
        const dataStr = JSON.stringify(orderData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `order-${orderId}.json`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      this.error = error.message;
      console.error('Error exporting order:', error);
    }
  },

  openEditModal(order) {
    this.editingOrder = { ...order };
    
    // Populate edit form with current order data
    this.editForm = {
      customerName: order.customerName || '',
      customerPhone: order.customerPhone || '',
      customerEmail: order.customerEmail || order.userEmail || '',
      status: order.status || 'pending',
      deliveryAddress: order.deliveryAddress || '',
      specialRequest: order.specialRequest || '',
      paymentMethod: order.paymentMethod || 'cash',
      subtotal: order.subtotal || 0,
      serviceFee: order.serviceFee || 0,
      totalAmount: order.totalAmount || 0,
      adminNotes: '',
             dishes: order.dishes ? order.dishes.map(dish => {
         // Try to find matching menu item by name
         const matchingMenuItem = this.menuItems.find(item => 
           item.name.toLowerCase() === dish.dishName?.toLowerCase()
         );
         return {
           ...dish,
           menuItemId: matchingMenuItem?.id || '' // Set menuItemId if found, otherwise empty
         };
       }) : []
    };
  },

  closeEditModal() {
    this.editingOrder = null;
    this.isUpdating = false;
    // Reset form
    this.editForm = {
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      status: 'pending',
      deliveryAddress: '',
      specialRequest: '',
      paymentMethod: 'cash',
      subtotal: 0,
      serviceFee: 0,
      totalAmount: 0,
      adminNotes: '',
      dishes: []
    };
  },

  async saveOrderChanges() {
    if (this.isUpdating) return;
    
    this.isUpdating = true;
    
    try {
      // Prepare update data
      const updateData = {
        customerName: this.editForm.customerName,
        customerPhone: this.editForm.customerPhone,
        customerEmail: this.editForm.customerEmail,
        status: this.editForm.status,
        deliveryAddress: this.editForm.deliveryAddress,
        specialRequest: this.editForm.specialRequest,
        paymentMethod: this.editForm.paymentMethod,
        subtotal: this.editForm.subtotal,
        serviceFee: this.editForm.serviceFee,
        totalAmount: this.editForm.totalAmount,
        adminNotes: this.editForm.adminNotes,
        dishes: this.editForm.dishes
      };

      // Call the admin service to update the order
      await adminOrderService.updateOrder(this.editingOrder.id, updateData);

      // Update the order in the local array
      const orderIndex = this.orders.findIndex(order => order.id === this.editingOrder.id);
      if (orderIndex !== -1) {
        Object.assign(this.orders[orderIndex], updateData);
        this.orders[orderIndex] = adminOrderService.formatOrderForDisplay(this.orders[orderIndex]);
      }

      // Update selected order if it's the same
      if (this.selectedOrder && this.selectedOrder.id === this.editingOrder.id) {
        Object.assign(this.selectedOrder, updateData);
        this.selectedOrder = adminOrderService.formatOrderForDisplay(this.selectedOrder);
      }

      this.closeEditModal();
      
      // Show success message (you can add a toast notification here)
      console.log('Order updated successfully');
      
    } catch (error) {
      this.error = error.message;
      console.error('Error updating order:', error);
    } finally {
      this.isUpdating = false;
    }
  },

     addNewDish() {
     this.editForm.dishes.push({
       dishName: '',
       count: 1,
       price: 0,
       specialRequest: '',
       menuItemId: '' // Add this field to track selected menu item
     });
   },

  removeDish(index) {
    this.editForm.dishes.splice(index, 1);
    this.calculateTotals();
  },

     calculateTotals() {
     // Calculate subtotal from dishes
     const subtotal = this.editForm.dishes.reduce((total, dish) => {
       return total + ((dish.price || 0) * (dish.count || 0));
     }, 0);
     
     // Update subtotal
     this.editForm.subtotal = subtotal;
     
     // Calculate total (subtotal + service fee)
     this.editForm.totalAmount = subtotal + (this.editForm.serviceFee || 0);
   },

   onMenuItemSelect(dish, selectedMenuItemId) {
     if (selectedMenuItemId) {
       const selectedItem = this.menuItems.find(item => item.id === selectedMenuItemId);
       if (selectedItem) {
         dish.dishName = selectedItem.name;
         dish.price = selectedItem.price;
         this.calculateTotals();
       }
     } else {
       // Reset when no item is selected
       dish.dishName = '';
       dish.price = 0;
       this.calculateTotals();
     }
   },

   getDishDisplayName(dish) {
     // Use translation utility for dish name
     return getTranslatedDishName(dish.dishName, this.$t);
   },

  handleLogout() {
    this.$router.push('/admin/login');
  },

  getStatusOptions(orderType) {
    if (orderType === 'delivery') {
      return [
        { value: 'pending', label: 'admin.orders.statuses.pending' },
        { value: 'preparing', label: 'admin.orders.statuses.preparing' },
        { value: 'confirmed', label: 'admin.orders.statuses.confirmed' },
        { value: 'cancelled', label: 'admin.orders.statuses.cancelled' },
        { value: 'delivered', label: 'admin.orders.statuses.delivered' },
      ];
    } else {
      return [
        { value: 'notStarted', label: 'admin.orders.statuses.notStarted' },
        { value: 'inProgress', label: 'admin.orders.statuses.inProgress' },
        { value: 'cancelled', label: 'admin.orders.statuses.cancelled' },
        { value: 'served', label: 'admin.orders.statuses.served' },
      ];
    }
  },
}}
</script>

<style scoped>
/* Add any component-specific styles here */
</style>